import { computed, defineComponent, unref, watchEffect } from 'vue'
import type { IFormWizardProps } from './types'
import BreadcrumbStepper from 'components/stepper/breadcrumb/index'
import Button from '../button'
import { useAsyncAction } from '@/utils/async/useAsyncAction'
import ProgressBar from '../progress-bar'
import { useResponsive } from '~/composables/useResponsive'

const FormWizard = defineComponent<IFormWizardProps>({
  name: 'FormWizard',
  setup(props) {
    const responsive = useResponsive();
    const progress = computed(() => unref(props.handler.props.progress))
    const nextButtonText = computed(() => {
      return unref(props.handler.props.nextButtonText) || 'Next'
    })

    const saveButtonText = computed(() => {
      return unref(props.handler.props.nextButtonText) || 'Save'
    })

    const nextButtonDisabled = computed(() => {
      return unref(props.handler.props.nextButtonDisabled) === true
    })

    const actionSave = useAsyncAction(async () => {
      if (props.handler.props.onClickNext) {
        await props.handler.props.onClickNext();
      }
      await props.handler.handleSave()
    })

    const errors = computed(() => unref(props.handler.props.errors) || [])

    const actionGoNext = useAsyncAction(async () => {
      if (props.handler.props.onClickNext) {
        await props.handler.props.onClickNext();
      }
      if (errors.value.length > 0) return
      const formHandler = props.handler.step.value.formHandler
      if (formHandler) {
        const errorCount = await formHandler.errors.refresh({ forceVisible: true })
        if (errorCount > 0) return
      }
      await props.handler.handleGoNext()
    })

    const hideActions = computed(() => !!unref(props.handler.props.hideActions))
    const showFooter = computed(() => !!unref(props.handler.props.showFooter) && !hideActions.value)

    const isLoading = computed(
      () =>
        props.handler.state.loading ||
        actionSave.loading.value ||
        unref(props.handler.props.loading) ||
        false
    )

    const hideGoBack = computed(() => !!unref(props.handler.props.hideGoBack))
    console.log("PROPS IN FORMWIZARD COMPONENT", props)

    watchEffect(() => {
      console.log('hide', hideGoBack.value)
    })

    return () => {
      return (
        <div class="flex flex-col h-full w-full overflow-hidden relative">
          {/* TOP SECTION */}
          {!props.handler.props.noTopSection && (
            <div class="w-full shrink-0 flex flex-row gap-2 items-center md:justify-between p-4 md:p-6 border-b border-gray-300 text-lg font-semibold">
              {(props.handler.step.value.title || props.handler.props.globalTitle) && (
                <h2 class="md:flex-1 text-gray-900 text-sm md:text-lg">
                  {props.handler.props.globalTitle || props.handler.step.value.title}
                  {props.handler.props.titleBody ? <props.handler.props.titleBody /> : null}
                </h2>
              )}
              {responsive.isSmall.value &&
                <div class="w-0 h-3  outline-solid outline-1 outline-offset-[-0.50px] outline-gray-400"></div>
              }
              {props.handler.titles.value.length > 0 && (
                <BreadcrumbStepper
                  steps={props.handler.titles.value}
                  activeStep={props.handler.state.index}
                />
              )}
              {!props.handler.props.noCloseButton && (
                <div class="flex flex-1 flex-row justify-end text-gray-800">
                  <button
                    onClick={props.handler.handleClose}
                    type="button"
                    class="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white"
                    data-modal-hide="default-modal"
                  >
                    <svg
                      class="w-3 h-3"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 14 14"
                    >
                      <path
                        stroke="currentColor"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          )}

          {/* BODY */}
          <div
            class={[
              'flex-1 flex flex-col overflow-y-auto w-full relative',
              isLoading.value || unref(props.handler.props.noTopPadding) ? '' : responsive.isSmall.value ? '' : 'py-12',
              unref(props.handler.props.wrapperClass) || ''
            ]}
          >
            {props.handler.step.value.body && (
              <props.handler.step.value.body
                back={props.handler.handleGoBack}
                next={props.handler.handleGoNext}
              />
            )}
          </div>

          {/* FOOTER */}
          {showFooter.value && (
            <div class="flex flex-row shrink-0 flex-none justify-between p-5 border-t border-gray-300 text-lg font-semibold relative">
              {typeof progress.value === 'number' && (
                <div class="w-full absolute top-0 left-0">
                  <ProgressBar value={progress.value} scale100={true} size="xs" square />
                </div>
              )}
              {hideGoBack.value ? null : <Button isLoading={props.handler.state.goBackLoading} onClick={props.handler.handleGoBack}>Back</Button>}
              <div class="flex flex-row gap-4 ml-auto">
                <Button onClick={props.handler.handleCancel} variant="transparent" outlined>
                  Cancel
                </Button>
                {props.handler.props.customActionButton &&
                  props.handler.props.customActionButton
                }
                {
                  props.handler.state.index < props.handler.steps.value.length - 1 ? (
                    <Button
                      disabled={props.handler.nextIsDisabled.value || nextButtonDisabled.value}
                      isLoading={actionGoNext.loading.value}
                      onClick={actionGoNext.call}
                    >
                      {nextButtonText.value}
                    </Button>
                  ) : (
                    <Button
                      disabled={props.handler.nextIsDisabled.value || nextButtonDisabled.value}
                      isLoading={props.handler.state.loading || actionSave.loading.value}
                      onClick={actionSave.call}
                    >
                      {saveButtonText.value}
                    </Button>
                  )
                }
              </div>
            </div>
          )}
        </div>
      )
    }
  }
})

FormWizard.props = [
  'steps',
  'outputState',
  'onSave',
  'onCancel',
  'onGoBack',
  'formComponentExtensions',
  'noCloseButton',
  'noTopSection',
  'fullScreen',
  'globalTitle',
  'handler',
  'banner',
  'customActionButton'
]

export default FormWizard
